<template>
    <div class="spa-status-page">
        <!-- Page Header -->
        <div class="spa-header">
            <div class="d-flex align-center gap-3">
                <h1 class="d-flex align-center">
                    Spa Status
                    <small class="ml-3 text-medium-emphasis" v-if="monitorId">({{ monitorId }})</small>
                </h1>
                <v-dialog width="500">
                    <template v-slot:activator="{ props }">
                        <div v-bind="props" class="linkStateIndicator" :class="linkStateIndicatorClass" style="cursor:pointer"></div>
                    </template>
                    <template v-slot:default="{ isActive }">
                        <v-card title="Connection Status" :subtitle="`client id: ${clientId ?? 'generating...'}`">
                            <v-card-text>
                                <v-row>
                                    <v-col>
                                        <div class="text-caption text-medium-emphasis mb-1">Cloud</div>
                                        <strong class="linkStateText" :class="wssLinkStateIndicatorClass">{{ cloudConnectionStatusText }}</strong>
                                    </v-col>
                                    <v-col>
                                        <div class="text-caption text-medium-emphasis mb-1">Gateway</div>
                                        <strong class="linkStateText" :class="wssLinkStateIndicatorClass">{{ wssLinkStateText }}</strong>
                                    </v-col>
                                    <v-col>
                                        <div class="text-caption text-medium-emphasis mb-1">Spa</div>
                                        <strong class="linkStateText" :class="spaLinkStateIndicatorClass">{{ spaLinkStateText }}</strong>
                                    </v-col>
                                </v-row>
                                <v-row v-if="snackBarText">
                                    <v-col>
                                        <v-alert type="error" density="compact">{{ snackBarText }}</v-alert>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                            <v-card-actions>
                                <v-spacer></v-spacer>
                                <v-btn text="Close" @click="isActive.value = false" />
                            </v-card-actions>
                        </v-card>
                    </template>
                </v-dialog>
            </div>
        </div>

        <!-- Loading Skeleton -->
        <div v-if="showLoading" class="spa-content">
            <!-- Controls skeleton -->
            <Card class="mb-3">
                <template #title><Skeleton width="6rem" height="1rem" /></template>
                <template #content>
                    <div class="d-flex flex-wrap gap-3">
                        <Skeleton v-for="n in 4" :key="n" width="80px" height="32px" border-radius="4px" />
                        <Skeleton width="150px" height="32px" border-radius="4px" />
                        <Skeleton width="180px" height="32px" border-radius="4px" />
                    </div>
                </template>
            </Card>

            <!-- Quick Actions + RF + Timezone skeleton -->
            <v-row>
                <v-col cols="12" md="5">
                    <Card class="h-100">
                        <template #title><Skeleton width="7rem" height="1rem" /></template>
                        <template #content>
                            <div class="d-flex flex-wrap gap-2">
                                <Skeleton v-for="n in 4" :key="n" width="130px" height="32px" border-radius="4px" />
                            </div>
                        </template>
                    </Card>
                </v-col>
                <v-col cols="12" md="3">
                    <Card class="h-100">
                        <template #title><Skeleton width="5rem" height="1rem" /></template>
                        <template #content>
                            <Skeleton width="120px" height="40px" border-radius="4px" />
                        </template>
                    </Card>
                </v-col>
                <v-col cols="12" md="4">
                    <Card class="h-100">
                        <template #title><Skeleton width="5rem" height="1rem" /></template>
                        <template #content>
                            <Skeleton width="100%" height="40px" border-radius="4px" />
                        </template>
                    </Card>
                </v-col>
            </v-row>

            <!-- Temperature + Identity skeleton -->
            <v-row class="mb-3">
                <v-col cols="12" md="6">
                    <Card>
                        <template #title><Skeleton width="9rem" height="1rem" /></template>
                        <template #content>
                            <div class="d-flex flex-column gap-2">
                                <Skeleton v-for="n in 5" :key="n" width="100%" height="1.2rem" />
                            </div>
                        </template>
                    </Card>
                </v-col>
                <v-col cols="12" md="6">
                    <Card>
                        <template #title><Skeleton width="4rem" height="1rem" /></template>
                        <template #content>
                            <div class="d-flex flex-column gap-2">
                                <Skeleton v-for="n in 7" :key="n" width="100%" height="1.2rem" />
                            </div>
                        </template>
                    </Card>
                </v-col>
            </v-row>

            <!-- Live Stream skeleton -->
            <Card>
                <template #title><Skeleton width="8rem" height="1rem" /></template>
                <template #content>
                    <Skeleton width="100%" height="120px" border-radius="4px" />
                </template>
            </Card>
        </div>

        <div v-if="connected && spaState" class="spa-content">
            <div class="spa-main-layout">
            <!-- Left: main content -->
            <div class="spa-main-left">
            <!-- Controls: Zone buttons + Set Point -->
            <Card class="mb-3">
                <template #title>Controls</template>
                <template #content>
                    <div class="d-flex flex-wrap align-center gap-3">
                        <div v-for="pump of pumps" :key="pump.id" class="pump-card" :class="{ 'pump-card--active': pump.active }" @click="togglePump(pump)">
                            <div class="pump-card__header">
                                <v-icon size="14" :color="colorForPumpState(pump)">mdi-pump</v-icon>
                                <span class="pump-card__name">Pump {{ pump.id }}</span>
                                <span class="pump-card__speed" v-if="pump.active && pump.maxSpeed > 0">
                                    {{ Math.round((pump.speed ?? 0) / pump.maxSpeed * 100) }}%
                                </span>
                                <span class="pump-card__speed pump-card__speed--off" v-else>OFF</span>
                            </div>
                            <div class="pump-card__initiators" v-if="lastKnownInitiators[pump.id]?.length > 0">
                                <v-chip
                                    v-for="init of lastKnownInitiators[pump.id]"
                                    :key="init"
                                    size="x-small"
                                    :color="initiatorColor(init)"
                                    variant="flat"
                                    class="pump-initiator-chip"
                                >{{ initiatorLabel(init) }}</v-chip>
                            </div>
                        </div>
                        <v-btn v-for="waterfall of waterfalls" :key="waterfall.id" :color="colorForWaterfallState(waterfall)" size="small" @click="toggleWaterfall(waterfall)">
                            Waterfall {{ waterfall.id }}
                        </v-btn>
                        <div v-for="light of lights" :key="light.id" class="zone-card zone-card--light" :class="{ 'zone-card--active': light.active }" @click="toggleLight(light)">
                            <div class="zone-card__header">
                                <v-icon size="14" :color="colorForLightState(light)">mdi-lightbulb</v-icon>
                                <span class="zone-card__name">Light {{ light.id }}</span>
                                <span class="zone-card__status" :class="light.active ? 'zone-card__status--on' : 'zone-card__status--off'">
                                    {{ light.active ? 'ON' : 'OFF' }}
                                </span>
                            </div>
                        </div>
                        <span class="ctrl-sep"></span>
                        <v-text-field
                            v-model.number="setPointF"
                            label="Set Point °F"
                            clearable
                            :disabled="!spaConnected"
                            hide-details
                            density="compact"
                            style="width: 150px; flex: 0 0 auto"
                        />
                        <v-btn @click="updateSetPoint" size="small" :disabled="!spaConnected">Update</v-btn>
                        <span class="ctrl-sep"></span>
                        <v-select
                            v-model="selectedOperationMode"
                            :items="operationModeOptions"
                            item-title="label"
                            item-value="value"
                            label="Operation Mode"
                            density="compact"
                            hide-details
                            :disabled="!spaConnected"
                            style="width: 180px; flex: 0 0 auto"
                            @update:model-value="debouncedUpdateOperationMode"
                        />
                    </div>
                </template>
            </Card>

            <!-- Quick Actions + RF + Timezone -->
            <v-row class="mb-3">
                <v-col cols="12" md="5">
                    <Card class="h-100">
                        <template #title>Quick Actions</template>
                        <template #content>
                            <div class="d-flex flex-wrap gap-2">
                                <v-btn size="small" :disabled="!connected" @click="sendRebootCommand(DeviceType.IN_TOUCH_3_EN)">Reboot in.touch EN</v-btn>
                                <v-btn size="small" :disabled="!spaConnected" @click="sendRebootCommand(DeviceType.IN_TOUCH_3_CO)">Reboot in.touch CO</v-btn>
                                <v-btn size="small" :disabled="!spaConnected" @click="sendRebootCommand(DeviceType.WATERLAB)">Reboot Waterlab</v-btn>
                                <v-btn size="small" :disabled="!spaConnected" @click="requestWaterlabSample()">Request Water Sample</v-btn>
                            </div>
                        </template>
                    </Card>
                </v-col>
                <v-col cols="12" md="3">
                    <Card class="h-100">
                        <template #title>RF Channel</template>
                        <template #content>
                            <div class="d-flex align-center gap-3 flex-wrap">
                                <v-text-field
                                    v-model.number="rfChannelInput"
                                    label="Channel"
                                    type="number"
                                    :min="rfChannelRange?.min"
                                    :max="rfChannelRange?.max"
                                    :hint="`Range: ${rfChannelRange?.min}–${rfChannelRange?.max} | Current: ${currentRfChannel ?? 'unknown'}`"
                                    persistent-hint
                                    :disabled="!connected"
                                    density="compact"
                                    style="width: 120px"
                                    @update:model-value="debouncedUpdateRfChannel"
                                />
                                <v-chip :color="rfStrengthColor" size="small">Signal: {{ rfStrengthText }}</v-chip>
                            </div>
                        </template>
                    </Card>
                </v-col>
                <v-col cols="12" md="4">
                    <Card class="h-100">
                        <template #title>Timezone</template>
                        <template #content>
                            <v-autocomplete
                                v-model="selectedTimezoneLabel"
                                :items="allTimezoneLabels"
                                label="Timezone"
                                density="compact"
                                hide-details
                                :disabled="!spaConnected"
                                :error="!currentSpaTimezone"
                                @update:model-value="debouncedUpdateTimezone"
                            />
                            <div class="mt-2 text-caption text-medium-emphasis" v-if="currentSpaTimezone">
                                <span>Offset: {{ timezoneOffsetLabel }}</span>
                                <span class="ml-3">Spa time: {{ spaLocalTime }}</span>
                            </div>
                            <div class="mt-2 text-caption text-error" v-else>
                                No timezone set
                            </div>
                            <v-alert
                                type="warning"
                                variant="tonal"
                                density="compact"
                                class="mt-3"
                            >
                                <span class="text-caption">Only change for debugging / support. Timezone should normally be adjusted via the spa location.</span>
                            </v-alert>
                        </template>
                    </Card>
                </v-col>
            </v-row>

            <!-- Temperature + Identity side by side -->
            <v-row class="mb-3" v-if="heater !== undefined">
                <v-col cols="12" md="6">
                    <Card class="h-100">
                        <template #title>Temperature Control</template>
                        <template #content>
                            <v-table class="text-left info-table" density="compact">
                                <tbody>
                                    <tr>
                                        <th scope="col" class="label-col"></th>
                                        <th scope="col">°F</th>
                                        <th scope="col">°C</th>
                                    </tr>
                                    <tr>
                                        <th scope="row" class="label-col">Heater SetPoint:</th>
                                        <td>{{ heater?.setPoint !== undefined ? round(celsiusToFahrenheit(heater?.setPoint)) : '--' }} °F</td>
                                        <td>{{ heater?.setPoint !== undefined ? heater?.setPoint : '--' }} °C</td>
                                    </tr>
                                    <tr>
                                        <th scope="row" class="label-col">Min. SetPoint:</th>
                                        <td>{{ round(celsiusToFahrenheit(heater?.minTemperatureSetPointC)) }} °F</td>
                                        <td>{{ heater?.minTemperatureSetPointC }} °C</td>
                                    </tr>
                                    <tr>
                                        <th scope="row" class="label-col">Max. SetPoint:</th>
                                        <td>{{ round(celsiusToFahrenheit(heater?.maxTemperatureSetPointC)) }} °F</td>
                                        <td>{{ heater?.maxTemperatureSetPointC }} °C</td>
                                    </tr>
                                    <tr>
                                        <th scope="row" class="label-col">Heater State:</th>
                                        <td>{{ heaterStatusText }}</td>
                                        <td></td>
                                    </tr>
                                </tbody>
                            </v-table>
                        </template>
                    </Card>
                </v-col>
                <v-col cols="12" md="6">
                    <Card class="h-100">
                        <template #title>Identity</template>
                        <template #content>
                            <v-table class="text-left info-table" density="compact">
                                <tbody>
                                    <tr>
                                        <th scope="row" class="label-col">Configuration:</th>
                                        <td>{{ vesselIdentity?.spaConfigId_ }}</td>
                                    </tr>
                                    <tr>
                                        <th scope="row" class="label-col">in.touch EN Version:</th>
                                        <td>{{ vesselIdentity?.en_?.fwVersion ?? '—' }}</td>
                                    </tr>
                                    <tr>
                                        <th scope="row" class="label-col">in.touch EN S/N:</th>
                                        <td>{{ vesselIdentity?.en_?.sn ?? '—' }}</td>
                                    </tr>
                                    <tr>
                                        <th scope="row" class="label-col">in.touch CO Version:</th>
                                        <td>{{ vesselIdentity?.co_?.fwVersion ?? '—' }}</td>
                                    </tr>
                                    <tr>
                                        <th scope="row" class="label-col">in.touch CO S/N:</th>
                                        <td>{{ vesselIdentity?.co_?.sn ?? '—' }}</td>
                                    </tr>
                                    <tr>
                                        <th scope="row" class="label-col">Waterlab Version:</th>
                                        <td>{{ vesselIdentity?.waterlab_?.fwVersion ?? '—' }}</td>
                                    </tr>
                                    <tr>
                                        <th scope="row" class="label-col">Waterlab S/N:</th>
                                        <td>{{ vesselIdentity?.waterlab_?.sn ?? '—' }}</td>
                                    </tr>
                                </tbody>
                            </v-table>
                        </template>
                    </Card>
                </v-col>
            </v-row>
            </div>
            <!-- Right: Live Stream sidebar spanning full height -->
            <div class="spa-main-right">
                <Card class="h-100">
                    <template #title>Sample Live Stream</template>
                    <template #content>
                        <monitor-live-stream v-if="monitorId" :monitorId="monitorId" />
                    </template>
                </Card>
            </div>
            </div>
        </div>

        <!-- Quick Actions shown even when disconnected -->
        <div v-if="!showLoading && (!connected || !spaState)" class="mt-6">
            <Card>
                <template #title>Quick Actions</template>
                <template #content>
                    <div class="d-flex flex-wrap gap-2">
                        <v-btn :disabled="!connected" @click="sendRebootCommand(DeviceType.IN_TOUCH_3_EN)">Reboot in.touch EN</v-btn>
                        <v-btn :disabled="!spaConnected" @click="sendRebootCommand(DeviceType.IN_TOUCH_3_CO)">Reboot in.touch CO</v-btn>
                        <v-btn :disabled="!spaConnected" @click="sendRebootCommand(DeviceType.WATERLAB)">Reboot Waterlab</v-btn>
                        <v-btn :disabled="!spaConnected" @click="requestWaterlabSample()">Request Water Sample</v-btn>
                    </div>
                </template>
            </Card>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { useApi } from '@/api'
import { rfStrengthLabel } from '@/lib/vesselHealth'
import { useBreadCrumbsStore } from '@/stores/breadCrumbs'
import { useVesselStore } from '@/stores/vessel'
import { useAuth0 } from '@auth0/auth0-vue'
import { celsiusToFahrenheit, fahrenheitToCelsius, round } from '@geckoal/gecko-spa-api'
import MonitorLiveStream from '../MonitorLiveStream.vue'
import Card from 'primevue/card'
import Skeleton from 'primevue/skeleton'
import {
    MessageTransporter,
    MqttTransporterStrategy,
    TransporterConnectionStatus,
    TransporterEvent,
    VesselClient,
    VesselIdentityEvent,
    VesselStateEvent,
    RebootCommand,
    RequestWaterlabSample,
    DeviceType,
    type FlowZone,
    type LightingZone,
    type MqttSession,
    type TemperatureControlZone
} from '@geckoal/vessel-client'
import type { TransporterEventCallback } from '@geckoal/vessel-client/src/lib/messageHandler/transporterEventListener'
import {
    FlowZoneInitiator,
    GatewayConnectionStatus,
    OperationMode,
    RfStrength,
    TemperatureControlZoneStatus,
    VesselConnectionStatus,
    type IdentityShadowV1,
    type StaticSpaConfigV1,
    type StaticStateVesselV1
} from '@geckoal/web-models'
import { computed, onMounted, ref, watch, type Ref } from 'vue'
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import { getTimezoneOffset } from 'date-fns-tz'
import { debounce } from '@geckoal/gecko-webapp-utils'

const vesselStore = useVesselStore()
const breadCrumbsStore = useBreadCrumbsStore()
const user = useAuth0().user
const toast = useToast()

const spaState = ref<StaticStateVesselV1 | undefined>()
const rfChannelInput = ref<number | undefined>()

const currentRfChannel = computed(() => spaState.value?.features?.rf?.channel)
const rfChannelRange = computed(() => vesselClient?.getRFChannelRange())

// Shared with the vessel Health Checks card so both views word the signal identically.
const rfStrengthText = computed(() => rfStrengthLabel(spaState.value?.features?.rf?.strength_))

const rfStrengthColor = computed(() => {
    const strength = spaState.value?.features?.rf?.strength_
    if (strength === undefined) return 'grey'
    if (strength <= RfStrength.WEAK) return 'error'
    if (strength === RfStrength.FAIR) return 'warning'
    return 'success'
})

const updateRfChannel = async () => {
    if (rfChannelInput.value === undefined) return
    const desired = rfChannelInput.value
    try {
        await vesselClient?.setChannelRF(desired)
        pendingRfChannel.value = desired
        toast.add({ severity: 'info', summary: 'Setting RF channel…', group: 'rf-pending' })
    } catch {
        toast.add({ severity: 'error', summary: 'Failed to update RF channel', life: 4000 })
    }
}

const debouncedUpdateRfChannel = debounce(updateRfChannel, 800)

const operationModeOptions = [
    { label: 'Away', value: OperationMode.AWAY },
    { label: 'Standard', value: OperationMode.STANDARD },
    { label: 'Savings', value: OperationMode.SAVINGS },
    { label: 'Super Savings', value: OperationMode.SUPER_SAVINGS },
    { label: 'Weekender', value: OperationMode.WEEKENDER },
    { label: 'Other', value: OperationMode.OTHER },
]

const selectedOperationMode = ref<OperationMode | undefined>()
const pendingOperationMode = ref<OperationMode | undefined>()
const pendingRfChannel = ref<number | undefined>()

const updateOperationMode = async () => {
    if (selectedOperationMode.value === undefined) return
    const desired = selectedOperationMode.value
    try {
        await vesselClient?.setOperationMode(desired)
        pendingOperationMode.value = desired
        toast.add({ severity: 'info', summary: 'Setting operation mode…', group: 'opmode-pending' })
    } catch {
        toast.add({ severity: 'error', summary: 'Failed to update operation mode', life: 4000 })
    }
}

const debouncedUpdateOperationMode = debounce(updateOperationMode, 800)

// Timezone
const allTimezoneLabels: string[] = (Intl as any).supportedValuesOf('timeZone')
const selectedTimezoneLabel = ref<string | undefined>()
const pendingTimezone = ref<string | undefined>()

const currentSpaTimezone = computed(() => spaState.value?.features?.timezone)

const timezoneOffsetLabel = computed(() => {
    const offset = currentSpaTimezone.value?.offset
    if (offset === undefined) return ''
    const sign = offset >= 0 ? '+' : '-'
    const abs = Math.abs(offset)
    const h = Math.floor(abs / 60).toString().padStart(2, '0')
    const m = (abs % 60).toString().padStart(2, '0')
    return `UTC${sign}${h}:${m} (${offset >= 0 ? '+' : ''}${offset} min)`
})

const spaLocalTime = computed(() => {
    const offset = currentSpaTimezone.value?.offset
    if (offset === undefined) return ''
    const utcMs = now.value.getTime() + now.value.getTimezoneOffset() * 60_000
    const spaMs = utcMs + offset * 60_000
    return new Date(spaMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
})

const getOffsetMinutesForZone = (ianaZone: string): number => {
    try {
        // getTimezoneOffset returns offset in milliseconds
        return getTimezoneOffset(ianaZone, now.value) / 60_000
    } catch {
        return 0
    }
}

const updateTimezone = async () => {
    if (selectedTimezoneLabel.value === undefined) return
    const offset = getOffsetMinutesForZone(selectedTimezoneLabel.value)
    try {
        await vesselClient?.setTimezone({ id: selectedTimezoneLabel.value, offset })
        pendingTimezone.value = selectedTimezoneLabel.value
        toast.add({ severity: 'info', summary: 'Setting timezone…', group: 'tz-pending' })
    } catch {
        toast.add({ severity: 'error', summary: 'Failed to update timezone', life: 4000 })
    }
}

const debouncedUpdateTimezone = debounce(updateTimezone, 800)

watch(currentRfChannel, (val) => {
    if (val !== undefined && rfChannelInput.value === undefined) {
        rfChannelInput.value = val
    }
    // Confirm pending RF channel change
    if (pendingRfChannel.value !== undefined && val === pendingRfChannel.value) {
        toast.removeGroup('rf-pending')
        toast.add({ severity: 'success', summary: 'RF channel updated', life: 3000 })
        pendingRfChannel.value = undefined
    }
})

const cloudConnectionStatus = ref<TransporterConnectionStatus>(TransporterConnectionStatus.UNKNOWN)
const inboundMessages = ref<string[]>([])
const outboundMessages = ref<string[]>([])
const setPointF = ref<number | undefined>()
const snackBarText = ref<string | undefined>()
const shouldBeConnected = ref(true)
const clientId: Ref<string | undefined> = ref()
const isInConnection = ref(false)
const vesselIdentity = ref<IdentityShadowV1>()

const props = defineProps({
    accountId: {
        type: Number,
        required: false
    },
    vesselId: {
        type: Number,
        required: false
    },
    monitorIdProp: {
        type: String,
        required: false
    }
})

/** Resolved account ID — set during init for the monitor-only route so reconnects can refetch the spa config */
const resolvedAccountId = ref<number | undefined>()

function sendRebootCommand(deviceType: DeviceType) {
    vesselClient?.sendCommand(new RebootCommand({ deviceType }))
}

function requestWaterlabSample() {
    vesselClient?.sendCommand(new RequestWaterlabSample())
}

const linkStateColors = {
    UNAVAILABLE: 'gray',
    OFFLINE: 'red',
    LOADING: 'orange',
    ONLINE: 'green'
}

let clientIdNeedsGeneration = true

let vesselClient: VesselClient | undefined = undefined
let spaConfig: StaticSpaConfigV1 | undefined = undefined

const now = ref(new Date())
let clockInterval: ReturnType<typeof globalThis.setInterval> | undefined

onMounted(async function init() {
    clockInterval = globalThis.setInterval(() => { now.value = new Date() }, 1000)
    _updateBreadCrumb()

    if (props.monitorIdProp) {
        // Monitor-only route: look up the owning account so we can fetch the spa configuration
        let accountId: number | undefined
        try {
            const details = await useApi().getMonitorDetailsV1({ monitorId: props.monitorIdProp })
            accountId = details.owningAccountId
        } catch (e) {
            console.warn('Could not fetch monitor details for spa config — controls may be limited', e)
        }
        resolvedAccountId.value = accountId
        await connect(props.monitorIdProp, accountId)
    } else if (props.accountId !== undefined && props.vesselId !== undefined) {
        await vesselStore.loadVessel(props.accountId, props.vesselId)
        const mid = vesselStore.vessel?.monitor?.monitorId
        if (mid) await connect(mid, props.accountId)
    }
})

onBeforeRouteUpdate(() => {
    shouldBeConnected.value = true
})

onBeforeRouteLeave(async (_to, _from, next) => {
    shouldBeConnected.value = false
    if (clockInterval !== undefined) clearInterval(clockInterval)
    await disconnectFromSpa()
    console.log('DISCONNECTED FROM SPA')
    next()
})

function _updateBreadCrumb() {
    const items: { text: string; to?: object }[] = []

    if (props.accountId !== undefined) {
        items.push({
            text: `Account #${props.accountId}`,
            to: { name: 'AccountDetails', params: { accountId: props.accountId } }
        })
    }
    if (props.accountId !== undefined && props.vesselId !== undefined) {
        items.push({
            text: vesselStore.vessel?.name ?? `Vessel #${props.vesselId}`,
            to: { name: 'VesselDetails', params: { accountId: props.accountId, vesselId: props.vesselId } }
        })
    }
    if (props.monitorIdProp) {
        items.push({
            text: props.monitorIdProp,
            to: { name: 'MonitorDetails', params: { monitorId: props.monitorIdProp } }
        })
    }
    items.push({ text: 'Spa Status' })

    breadCrumbsStore.$patch({ items })
}

/**
 * TEMPORARY FIX UNTIL THE CHANGE IS MADE ON THE CONFIG
 * @deprecated
 */
const dictionaryToObjectWithId = (dictionary: { [key: string]: any }) => {
    return Object.entries(dictionary).reduce((acc, [key, value]) => {
        return { ...acc, [key]: { ...value, configId: key } }
    }, {})
}

/**
 * TEMPORARY FIX UNTIL THE CHANGE IS MADE ON THE CONFIG
 * @deprecated
 */
const fixConfigIdFromConfig = (config: StaticSpaConfigV1): StaticSpaConfigV1 => {
    const newConfig = { ...config }

    if (newConfig.zones?.flow) {
        newConfig.zones.flow = dictionaryToObjectWithId(newConfig.zones.flow)
    }

    if (newConfig.zones?.temperatureControl) {
        newConfig.zones.temperatureControl = dictionaryToObjectWithId(newConfig.zones.temperatureControl)
    }

    if (newConfig.zones?.lighting) {
        newConfig.zones.lighting = dictionaryToObjectWithId(newConfig.zones.lighting)
    }

    return newConfig
}

async function fetchSpaConfiguration(accountId: number, monitorId: string): Promise<StaticSpaConfigV1> {
    const spaConfig = (await useApi().getAccountMonitorConfigurationV1({
        accountId,
        monitorId
    })) as unknown as StaticSpaConfigV1

    return fixConfigIdFromConfig(spaConfig)
}

async function fetchLiveStreamSession(monitorId: string) {
    const liveStreamSession = await useApi().getMonitorLiveStreamV2({ monitorId: monitorId })
    return liveStreamSession
}

const vessel = computed(() => {
    return vesselStore.vessel
})

const monitorId = computed(() => {
    return props.monitorIdProp ?? vessel.value?.monitor?.monitorId
})

const cloudConnectionStatusText = computed(() => {
    if (cloudConnectionStatus.value === TransporterConnectionStatus.CONNECTED) return 'Connected'
    if (cloudConnectionStatus.value === TransporterConnectionStatus.DISCONNECTED) return 'Disconnected'
    return 'Not Connected'
})

const wssLinkStateText = computed(() => {
    if (wssLinkState.value === GatewayConnectionStatus.CONNECTED) return 'Connected'
    if (wssLinkState.value === GatewayConnectionStatus.DISCONNECTED) return 'Disconnected'
    return 'Not Connected'
})

const spaLinkStateText = computed(() => {
    if (spaLinkState.value === VesselConnectionStatus.RUNNING) return 'Connected'
    if (spaLinkState.value === VesselConnectionStatus.RF_DISCONNECTED) return 'Disconnected'
    if (spaLinkState.value === VesselConnectionStatus.RF_IN_PAIRING) return 'Pairing'
    return 'Not Connected'
})

const heaterStatusText = computed(() => {
    if (heater.value === undefined) return 'Not Connected'
    switch (heater.value.status_) {
        case TemperatureControlZoneStatus.IDLE:
            return 'Idle'
        case TemperatureControlZoneStatus.HEATING:
            return 'Heating'
        case TemperatureControlZoneStatus.COOLING:
            return 'Cooling'
        case TemperatureControlZoneStatus.SYS_ERROR:
            return 'Temperature Reading Invalid'
        default:
            return 'Unknown'
    }
})

const operationModeText = computed(() => {
    if (spaState.value === undefined) return 'Unavailable'

    // loop through the operation modes and return the correct string
    for (const mode of Object.values(OperationMode)) {
        if (mode === spaState.value.features?.operationMode) {
            return OperationMode[mode].toString()
        }
    }
    return 'Unknown'
})

const pumps = computed(() => {
    const flowZonesConfig = spaConfig?.zones?.flow
    const flowZones = spaState.value?.zones?.flow

    if (flowZonesConfig === undefined || flowZones === undefined) return undefined

    return Object.values(flowZonesConfig)
        .filter((flow) => flow.waterfalls?.length === 0 || flow.waterfalls === undefined)
        .map((flow) => flowZones[flow.configId as string] as FlowZone)
        .sort((a, b) => a.id.localeCompare(b.id))
})

const lights = computed(() => {
    const lightingZonesConfig = spaConfig?.zones?.lighting
    const lighthingZones = spaState.value?.zones?.lighting

    if (lightingZonesConfig === undefined || lighthingZones === undefined) return undefined

    return Object.values(lightingZonesConfig).map((lighthing) => lighthingZones[lighthing.configId as string] as LightingZone)
})

const waterfalls = computed(() => {
    const flowZonesConfig = spaConfig?.zones?.flow
    const flowZones = spaState.value?.zones?.flow

    if (flowZonesConfig === undefined || flowZones === undefined) return undefined

    return Object.values(flowZonesConfig)
        .filter((flow) => (flow.waterfalls?.length ?? 0) > 0)
        .map((flow) => flowZones[flow.configId as string] as FlowZone)
})

const heater = computed(() => {
    const tempControlsConfig = spaConfig?.zones?.temperatureControl
    const tempControlZones = spaState.value?.zones?.temperatureControl

    if (tempControlsConfig === undefined || tempControlZones === undefined) return undefined

    return Object.values(tempControlsConfig)
        .map((tempCtrl) => tempControlZones[tempCtrl.configId as string] as TemperatureControlZone)
        .at(0)
})

const heaterSetPointC = computed(() => {
    return heater.value?.setPoint
})

const wssLinkState = computed(() => {
    return spaState.value?.connectivity_?.gatewayStatus
})

const connected = computed(() => {
    return wssLinkState.value === GatewayConnectionStatus.CONNECTED
})

const spaLinkState = computed(() => {
    return spaState.value?.connectivity_?.vesselStatus
})

const spaConnected = computed(() => {
    return spaLinkState.value === VesselConnectionStatus.RUNNING
})

const linkStateIndicatorClass = computed(() => {
    if (wssLinkStateIndicatorClass.value === linkStateColors.UNAVAILABLE) return linkStateColors.UNAVAILABLE
    if (spaLinkStateIndicatorClass.value === linkStateColors.UNAVAILABLE) return linkStateColors.UNAVAILABLE

    if (wssLinkStateIndicatorClass.value === linkStateColors.OFFLINE) return linkStateColors.OFFLINE
    if (spaLinkStateIndicatorClass.value === linkStateColors.OFFLINE) return linkStateColors.OFFLINE

    if (wssLinkStateIndicatorClass.value === linkStateColors.LOADING) return linkStateColors.LOADING
    if (spaLinkStateIndicatorClass.value === linkStateColors.LOADING) return linkStateColors.LOADING
    return linkStateColors.ONLINE
})

const wssLinkStateIndicatorClass = computed(() => {
    if (wssLinkState.value === undefined || wssLinkState.value === GatewayConnectionStatus.UNKNOWN) return linkStateColors.UNAVAILABLE
    if (wssLinkState.value === GatewayConnectionStatus.DISCONNECTED) return linkStateColors.OFFLINE
    if (wssLinkState.value === GatewayConnectionStatus.CONNECTED) return linkStateColors.ONLINE
    return linkStateColors.LOADING
})

const spaLinkStateIndicatorClass = computed(() => {
    if (spaLinkState.value === undefined || spaLinkState.value === VesselConnectionStatus.UNKNOWN) return linkStateColors.UNAVAILABLE
    if (spaLinkState.value === VesselConnectionStatus.RF_DISCONNECTED) return linkStateColors.OFFLINE
    if (spaLinkState.value === VesselConnectionStatus.RF_IN_PAIRING) return linkStateColors.LOADING
    return linkStateColors.ONLINE
})

const showLoading = computed(() => {
    if (!isInConnection.value && vesselClient === undefined) return false
    if (vesselClient === undefined) return true
    if (wssLinkStateIndicatorClass.value === linkStateColors.LOADING) return true
    if (spaLinkStateIndicatorClass.value === linkStateColors.LOADING) return true
    if (spaState.value === undefined) return true
    return false
})

const vesselClientIsConnected = computed(() => {
    return vesselClient?.isConnected()
})

const connect = async (monitorId: string, accountId?: number) => {
    if (!isInConnection.value && vesselClientIsConnected.value) {
        console.log('Already connected')
        return
    }
    isInConnection.value = true

    inboundMessages.value = []
    outboundMessages.value = []

    const liveStreamSession = await fetchLiveStreamSession(monitorId)
    const mqttSession: MqttSession = {
        ...liveStreamSession,
        url: liveStreamSession.brokerUrl
    }
    const mqttStrategy = new MqttTransporterStrategy(mqttSession, { reconnectPeriod: 1000 * 30 })
    const transporter = new MessageTransporter(mqttStrategy)

    if (accountId !== undefined) {
        spaConfig = await fetchSpaConfiguration(accountId, monitorId)
    }
    vesselClient = new VesselClient(transporter, spaConfig ?? {} as StaticSpaConfigV1)

    // Event Handlers
    vesselClient.onUpdate(TransporterEvent.ERROR, socketError)
    vesselClient.onUpdate(TransporterEvent.CONNECTED, onCloudStatusUpdate as TransporterEventCallback)
    vesselClient.onUpdate(TransporterEvent.DISCONNECTED, onCloudStatusUpdate as TransporterEventCallback)
    vesselClient.onUpdate(TransporterEvent.EXPIRED_SESSION, restartConnection)
    vesselClient.onUpdate(VesselStateEvent.STATE_UPDATE, (_event, _state) => {
        onSpaStateUpdate(vesselClient?.getVesselState() as StaticStateVesselV1)
        vesselIdentity.value = vesselClient?.getIdentity() as IdentityShadowV1
    })
    vesselClient.onUpdate(VesselIdentityEvent.IDENTITY_UPDATE, () => {
        vesselIdentity.value = vesselClient?.getIdentity() as IdentityShadowV1
    })

    await vesselClient.connect()

    isInConnection.value = false
}

const restartConnection = async () => {
    const mid = props.monitorIdProp ?? monitorId.value
    if (mid === undefined) return
    await disconnectFromSpa()
    await connect(mid, props.accountId ?? resolvedAccountId.value)
}

const lastKnownInitiators = ref<Record<string, FlowZoneInitiator[]>>({})

const onSpaStateUpdate = (newSpaState: StaticStateVesselV1) => {
    // Cache initiators_ per zone whenever they arrive (the library wipes them on partial updates)
    const flowZones = newSpaState.zones?.flow
    if (flowZones) {
        for (const [zoneId, zone] of Object.entries(flowZones)) {
            if (zone?.initiators_ !== undefined) {
                lastKnownInitiators.value[zoneId] = zone.initiators_ as FlowZoneInitiator[]
            }
        }
    }
    spaState.value = newSpaState
}

const onCloudStatusUpdate = (event: TransporterEvent) => {
    if (event === TransporterEvent.CONNECTED) {
        cloudConnectionStatus.value = TransporterConnectionStatus.CONNECTED
        snackBarText.value = undefined
    } else if (event === TransporterEvent.DISCONNECTED) {
        cloudConnectionStatus.value = TransporterConnectionStatus.DISCONNECTED
    } else {
        cloudConnectionStatus.value = TransporterConnectionStatus.UNKNOWN
    }
}

const socketError = (reason: string) => {
    snackBarText.value = getErrorEventDetails(reason)
}

const disconnectFromSpa = async () => {
    // Dismiss any pending confirmation toasts
    if (pendingRfChannel.value !== undefined) {
        toast.removeGroup('rf-pending')
        toast.add({ severity: 'error', summary: 'RF channel update failed — spa disconnected', life: 4000 })
        pendingRfChannel.value = undefined
    }
    if (pendingOperationMode.value !== undefined) {
        toast.removeGroup('opmode-pending')
        toast.add({ severity: 'error', summary: 'Operation mode update failed — spa disconnected', life: 4000 })
        pendingOperationMode.value = undefined
    }
    if (pendingTimezone.value !== undefined) {
        toast.removeGroup('tz-pending')
        toast.add({ severity: 'error', summary: 'Timezone update failed — spa disconnected', life: 4000 })
        pendingTimezone.value = undefined
    }

    spaState.value = undefined
    lastKnownInitiators.value = {}
    inboundMessages.value = []
    outboundMessages.value = []

    spaConfig = undefined
    await vesselClient?.disconnect()
    vesselClient?.clearListeners()
    vesselClient = undefined
}

const colorForPumpState = (pump: FlowZone): string => {
    if (pump.incrementSpeed === 0) {
        if (pump.active) return 'blue-lighten-2'
        return 'grey-darken-1'
    } else {
        if (pump.speed === undefined || !pump.active) return 'grey-darken-1'
        if (pump.speed < pump.maxSpeed) return 'blue-lighten-2'
        if (pump.speed === pump.maxSpeed) return 'blue-lighten-1'
    }

    return 'grey-darken-1'
}

const colorForWaterfallState = (waterfall: FlowZone): string => {
    if (waterfall.active) return 'amber-lighten-2'
    return 'indigo-darken-3'
}

const colorForLightState = (light: LightingZone): string => {
    if (light.active) return 'yellow-darken-1'
    return 'grey-darken-1'
}

const togglePump = async ({ id, speed, active, maxSpeed, minSpeed, incrementSpeed }: FlowZone) => {
    // TURN ON
    if (active === undefined || active === false || speed === undefined) {
        await vesselClient?.setFlowZoneState(id, { active: true, speed: minSpeed })
        return
    }

    // Flowzone is active — cycle speed up
    if (speed < maxSpeed) {
        await vesselClient?.setFlowZoneState(id, { active: true, speed: Math.min(speed + incrementSpeed, maxSpeed) })
        return
    }

    // At max speed — determine whether we can fully turn off or just drop to min
    const initiators = lastKnownInitiators.value[id] ?? []
    const hasNonUserInitiators = initiators.some(i => i !== FlowZoneInitiator.USER_DEMAND)

    if (hasNonUserInitiators) {
        // Other initiators (heating, filtration, etc.) are keeping the pump alive,
        // so drop back to min speed instead of turning off entirely
        await vesselClient?.setFlowZoneState(id, { active: true, speed: minSpeed })
    } else {
        // Only user demand (or nothing) — safe to turn off
        await vesselClient?.setFlowZoneState(id, { active: false, speed: minSpeed })
    }
}

const toggleWaterfall = async (waterfall: FlowZone) => {
    await vesselClient?.setFlowZoneActivationStatus(waterfall.id, !waterfall.active)
}

const toggleLight = async (light: LightingZone) => {
    await vesselClient?.setLightingZoneActivationStatus(light.id, !light.active)
}

const updateSetPoint = async () => {
    if (heater.value === undefined) throw new Error(`no heater`)
    if (setPointF.value === undefined) throw new Error(`not connected`)
    await vesselClient?.setTemperatureControlZoneSetPoint(heater.value.id, fahrenheitToCelsius(setPointF.value))
}

const getErrorEventDetails = (eventName: string) => {
    switch (eventName) {
        case 'ECONNREFUSED':
            return 'Connection Refused'
        case 'ECONNRESET':
            return 'Connection Reset'
        case 'EADDRINUSE':
            return 'Address In Use'
        case 'ENOTFOUND':
            return 'Not Found'
        default:
            return 'Unknown Error'
    }
}

watch(heaterSetPointC, () => {
    if (heaterSetPointC.value === undefined) return undefined
    setPointF.value = celsiusToFahrenheit(heaterSetPointC.value)
})

watch(
    () => spaState.value?.features?.operationMode,
    (val) => {
        if (val !== undefined && selectedOperationMode.value === undefined) {
            selectedOperationMode.value = val
        }
        // Confirm pending operation mode change
        if (pendingOperationMode.value !== undefined && val === pendingOperationMode.value) {
            toast.removeGroup('opmode-pending')
            toast.add({ severity: 'success', summary: 'Operation mode updated', life: 3000 })
            pendingOperationMode.value = undefined
        }
    }
)

watch(
    () => spaState.value?.features?.timezone?.id,
    (val) => {
        // Sync initial value
        if (val !== undefined && selectedTimezoneLabel.value === undefined) {
            selectedTimezoneLabel.value = val
        }
        // Confirm pending timezone change
        if (pendingTimezone.value !== undefined && val === pendingTimezone.value) {
            toast.removeGroup('tz-pending')
            toast.add({ severity: 'success', summary: 'Timezone updated', life: 3000 })
            pendingTimezone.value = undefined
        }
    }
)

const initiatorLabel = (init: FlowZoneInitiator): string => {
    switch (init) {
        case FlowZoneInitiator.USER_DEMAND: return 'User'
        case FlowZoneInitiator.CHECK_FLOW: return 'Check Flow'
        case FlowZoneInitiator.PURGE: return 'Purge'
        case FlowZoneInitiator.FILTRATION: return 'Filtration'
        case FlowZoneInitiator.HEATING: return 'Heating'
        case FlowZoneInitiator.HEAT_PUMP: return 'Heat Pump'
        case FlowZoneInitiator.COOLING: return 'Cooling'
        case FlowZoneInitiator.COOLDOWN: return 'Cooldown'
        default: return init
    }
}

const initiatorColor = (init: FlowZoneInitiator): string => {
    switch (init) {
        case FlowZoneInitiator.USER_DEMAND: return 'blue'
        case FlowZoneInitiator.HEATING: return 'deep-orange'
        case FlowZoneInitiator.HEAT_PUMP: return 'orange-darken-2'
        case FlowZoneInitiator.COOLING: return 'cyan'
        case FlowZoneInitiator.COOLDOWN: return 'light-blue'
        case FlowZoneInitiator.FILTRATION: return 'green'
        case FlowZoneInitiator.PURGE: return 'purple'
        case FlowZoneInitiator.CHECK_FLOW: return 'amber-darken-3'
        default: return 'grey-darken-1'
    }
}

watch(
    user,
    async (newValue) => {
        if (newValue != undefined && clientIdNeedsGeneration) {
            clientIdNeedsGeneration = false
            if (newValue.email == undefined) throw new Error(`Admin does not have an email address. Check auth0 configuration.`)

            const msgUint8 = new TextEncoder().encode(newValue.email) // encode as (utf-8) Uint8Array
            const hashBuffer = await crypto.subtle.digest('SHA-1', msgUint8) // hash the message
            const hashArray = Array.from(new Uint8Array(hashBuffer)) // convert buffer to byte array
            const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('') // convert bytes to hex string

            // Client Ids need to be exactly 39 chars (no more, no less)
            const baseId = ('IOS-admin-' + hashHex).substring(0, 39)
            clientId.value = baseId.padEnd(39, '0')
        }
    },
    { immediate: true }
)
</script>

<style lang="scss" scoped>
.spa-status-page {
    padding: 32px 24px;
}

.spa-header {
    margin-bottom: 24px;

    h1 {
        font-size: 1.75rem;
        font-weight: 600;
    }
}

.spa-content {
    display: flex;
    flex-direction: column;
    gap: 0;
}

.spa-main-layout {
    display: flex;
    gap: 16px;
    align-items: stretch;
}

.spa-main-left {
    flex: 1;
    min-width: 0;
}

.spa-main-right {
    width: 500px;
    flex-shrink: 0;

    :deep(.v-table__wrapper) {
        overflow-x: hidden;
    }
}

.ctrl-sep {
    display: inline-block;
    width: 1px;
    height: 28px;
    background: rgba(0,0,0,0.15);
    margin: 0 4px;
}

.info-table {
    th.label-col {
        width: 180px;
        font-weight: 500;
        white-space: nowrap;
        padding-right: 16px;
    }
    td {
        padding-top: 4px;
        padding-bottom: 4px;
    }
}

pre {
    white-space: pre-wrap;
    white-space: -moz-pre-wrap;
    white-space: -pre-wrap;
    white-space: -o-pre-wrap;
    word-wrap: break-word;
    margin-bottom: 10px;

    &:hover {
        background-color: #333;
    }
}

.yellow {
    color: gold;
    font-weight: bolder;
    font-size: 1.2rem;
}

$green: green;
$orange: orange;
$red: red;

.linkStateIndicator {
    width: 16px;
    height: 16px;
    background-color: gray;
    border-radius: 50%;
    display: inline-block;
    margin-left: 12px;
    flex-shrink: 0;

    &.red {
        background-color: $red;
    }

    &.orange {
        background-color: $orange;
    }

    &.green {
        background-color: $green;
    }
}

.linkStateText {
    &.red {
        color: $red;
    }

    &.orange {
        color: $orange;
    }

    &.green {
        color: $green;
    }
}

.pump-card {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 6px 10px;
    border-radius: 6px;
    background: rgba(0,0,0,0.25);
    border: 1px solid rgba(255,255,255,0.12);
    cursor: pointer;
    min-width: 90px;
    transition: background 0.15s, border-color 0.15s;

    &:hover {
        background: rgba(0,0,0,0.35);
        border-color: rgba(255,255,255,0.22);
    }

    &--active {
        border-color: rgba(100, 181, 246, 0.6);
        background: rgba(30, 80, 130, 0.35);
    }

    &__header {
        display: flex;
        align-items: center;
        gap: 5px;
        font-size: 0.8rem;
        font-weight: 500;
    }

    &__name {
        flex: 1;
    }

    &__speed {
        font-size: 0.75rem;
        font-weight: 600;
        color: #64b5f6;

        &--off {
            color: rgba(255,255,255,0.35);
            font-weight: 400;
        }
    }

    &__initiators {
        display: flex;
        flex-wrap: wrap;
        gap: 2px;
    }
}

.zone-card {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 6px 10px;
    border-radius: 6px;
    background: rgba(0,0,0,0.25);
    border: 1px solid rgba(255,255,255,0.12);
    cursor: pointer;
    min-width: 80px;
    transition: background 0.15s, border-color 0.15s;

    &:hover {
        background: rgba(0,0,0,0.35);
        border-color: rgba(255,255,255,0.22);
    }

    &--light.zone-card--active {
        border-color: rgba(255, 236, 100, 0.6);
        background: rgba(100, 90, 10, 0.35);
    }

    &__header {
        display: flex;
        align-items: center;
        gap: 5px;
        font-size: 0.8rem;
        font-weight: 500;
    }

    &__name {
        flex: 1;
    }

    &__status {
        font-size: 0.75rem;
        font-weight: 600;

        &--on {
            color: #ffee58;
        }

        &--off {
            color: rgba(255,255,255,0.35);
            font-weight: 400;
        }
    }
}

.pump-initiator-chip {
    font-size: 0.6rem !important;
    height: 16px !important;
}
</style>
